/**
 * Deep links to open a document in the in-app viewer (new tab).
 * Signed-in main app: /?doc=
 * Shared knowledge base: /shared/:linkId?doc=
 */

/**
 * Real Firestore ids only — exclude ephemeral editor drafts (`new_…`).
 * @param {string|null|undefined} documentId
 * @returns {boolean}
 */
export function isPersistedDocumentId(documentId) {
  const id = documentId != null ? String(documentId).trim() : '';
  return Boolean(id) && !id.startsWith('new_');
}

/**
 * @param {string} documentId
 * @param {string|null|undefined} [baseOrigin] App origin when not `window` host (e.g. embed generator).
 * @returns {string}
 */
export function buildSignedInDocumentViewerUrl(documentId, baseOrigin) {
  const id = documentId != null ? String(documentId).trim() : '';
  const origin =
    (baseOrigin != null && String(baseOrigin).trim()) ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  if (!id || !origin) return '/';
  return `${origin}/?doc=${encodeURIComponent(id)}`;
}

/**
 * @param {string|null|undefined} linkId Share link id (kbId)
 * @param {string} documentId
 * @param {string|null|undefined} [baseOrigin]
 * @returns {string}
 */
export function buildSharedDocumentViewerUrl(linkId, documentId, baseOrigin) {
  const lid = linkId != null ? String(linkId).trim() : '';
  const id = documentId != null ? String(documentId).trim() : '';
  const origin =
    (baseOrigin != null && String(baseOrigin).trim()) ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  if (!lid || !id || !origin) return '';
  return `${origin}/shared/${encodeURIComponent(lid)}?doc=${encodeURIComponent(id)}`;
}

/**
 * Set or clear the signed-in `doc` query param while preserving other search params.
 * @param {string|null|undefined} search `location.search` (with or without leading `?`)
 * @param {string|null|undefined} documentId Persisted id to set; null/temp ids remove `doc`
 * @returns {{ search: string, changed: boolean }} `search` has no leading `?` (react-router style)
 */
export function applySignedInDocSearchParam(search, documentId) {
  const raw =
    typeof search === 'string' && search.startsWith('?') ? search.slice(1) : search || '';
  const params = new URLSearchParams(raw);
  const current = params.get('doc');
  const currentNorm = current != null && String(current).trim() ? String(current).trim() : null;
  const desired = isPersistedDocumentId(documentId) ? String(documentId).trim() : null;

  if (desired) {
    if (currentNorm === desired) {
      return { search: params.toString(), changed: false };
    }
    params.set('doc', desired);
  } else {
    if (!currentNorm) {
      return { search: params.toString(), changed: false };
    }
    params.delete('doc');
  }

  return { search: params.toString(), changed: true };
}

/**
 * Map `/doc/:documentId` onto the canonical signed-in viewer location (`/?doc=`).
 * Preserves other query params and the hash. Temp drafts (`new_…`) drop `doc`.
 * @param {string|null|undefined} documentId
 * @param {string|null|undefined} search `location.search`
 * @param {string|null|undefined} hash `location.hash`
 * @returns {{ pathname: string, search: string, hash: string }}
 */
export function buildSignedInDocPathRedirectTo(documentId, search, hash) {
  const { search: nextSearch } = applySignedInDocSearchParam(search, documentId);
  const rawHash = typeof hash === 'string' ? hash : '';
  return {
    pathname: '/',
    search: nextSearch ? `?${nextSearch}` : '',
    hash: rawHash && !rawHash.startsWith('#') ? `#${rawHash}` : rawHash,
  };
}
